import { MaternityFile, PostnatalScheduleOverride } from '../../types'

export type PostnatalReviewKind = 'sixDays' | 'sixWeeks'

export type PostnatalReviewStatus = {
  kind: PostnatalReviewKind
  label: string
  scheduledDate: string
  originalDate: string
  isRescheduled: boolean
  isSuspended: boolean
  reason: string
  updatedBy: string
  isScheduled: boolean
  isActive: boolean
  isDue: boolean
  isOverdue: boolean
  isCompleted: boolean
  daysUntilDue: number | null
}

const startOfToday = () => {
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  return now
}

const parseDate = (value?: string) => {
  if (!value) return null
  const date = new Date(`${value}T00:00:00`)
  return Number.isNaN(date.getTime()) ? null : date
}

export function formatReviewDate(value?: string) {
  const date = parseDate(value)
  return date ? date.toLocaleDateString() : 'Not scheduled'
}

export function getPostnatalReviewStatus(
  file: MaternityFile,
  kind: PostnatalReviewKind,
): PostnatalReviewStatus {
  const originalDate =
    (kind === 'sixDays'
      ? file.dischargeSlip?.sixDaysPostnatalDate
      : file.dischargeSlip?.sixWeeksPostnatalDate) || ''
  const override: PostnatalScheduleOverride =
    file.postnatalScheduleOverrides?.[kind] || {}
  const scheduledDate = override.rescheduledDate || originalDate
  const visit =
    kind === 'sixDays'
      ? (file as any).postNatalOneWeek
      : (file as any).postNatalSixWeeks
  const isCompleted = Boolean(visit?.date)
  const isSuspended = Boolean(override.suspended) && !isCompleted
  const due = parseDate(scheduledDate)
  const today = startOfToday()
  const daysUntilDue = due
    ? Math.round((due.getTime() - today.getTime()) / 86400000)
    : null
  const reachedDueDate = daysUntilDue !== null && daysUntilDue <= 0

  return {
    kind,
    label: kind === 'sixDays' ? '6 Days' : '6 Weeks',
    scheduledDate,
    originalDate,
    isRescheduled: Boolean(
      override.rescheduledDate && override.rescheduledDate !== originalDate,
    ),
    isSuspended,
    reason: override.reason || '',
    updatedBy: override.updatedBy || '',
    isScheduled: Boolean(due),
    isActive: (reachedDueDate && !isSuspended) || isCompleted,
    isDue: reachedDueDate && !isCompleted && !isSuspended,
    isOverdue:
      daysUntilDue !== null && daysUntilDue < 0 && !isCompleted && !isSuspended,
    isCompleted,
    daysUntilDue,
  }
}

export function getDuePostnatalReviews(files: MaternityFile[]) {
  return files.flatMap((file) =>
    (['sixDays', 'sixWeeks'] as PostnatalReviewKind[])
      .map((kind) => ({
        file,
        status: getPostnatalReviewStatus(file, kind),
      }))
      .filter((entry) => entry.status.isDue),
  )
}

export function applyScheduleOverride(
  file: MaternityFile,
  kind: PostnatalReviewKind,
  patch: PostnatalScheduleOverride,
  updatedBy: string,
): MaternityFile {
  return {
    ...file,
    postnatalScheduleOverrides: {
      ...(file.postnatalScheduleOverrides || {}),
      [kind]: {
        ...(file.postnatalScheduleOverrides?.[kind] || {}),
        ...patch,
        updatedBy,
        updatedAt: new Date().toISOString(),
      },
    },
  }
}
